/**
 * V2ColorsSection - Editor section for V2 theme colors
 *
 * Identical to V1 ColorsSection — ThemeV2Colors has the same 3 color roles.
 * Kept as a separate component for full independence from V1.
 */

import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { SAccordion } from '@modules/ui-components';
import { ColorInput } from '@modules/reactFlow/components/shared/styleFields/ColorInput';
import type { ThemeV2Colors } from '../../types/themesV2.types';

// Field configuration for V2 colors
const COLOR_FIELDS: Array<{ key: keyof ThemeV2Colors; label: string; path: string }> = [
  { key: 'buttonsBackground', label: 'Button Background', path: 'colors.buttonsBackground' },
  { key: 'titlesSubtitles', label: 'Titles & Subtitles', path: 'colors.titlesSubtitles' },
  { key: 'buttonsTitle', label: 'Button Title', path: 'colors.buttonsTitle' },
];

export interface V2ColorsSectionProps {
  /** Current color values */
  colors: ThemeV2Colors;
  /** Callback when color changes */
  onChange: (path: string, value: unknown) => void;
  /** Whether section is disabled */
  disabled?: boolean;
  /** Whether section is expanded by default */
  defaultExpanded?: boolean;
}

/**
 * V2ColorsSection - SAccordion-based color editor for V2 themes
 */
export const V2ColorsSection: React.FC<V2ColorsSectionProps> = ({
  colors,
  onChange,
  disabled = false,
  defaultExpanded = true,
}) => {
  const handleColorChange = useCallback(
    (path: string) => (value: unknown) => {
      onChange(path, value);
    },
    [onChange]
  );

  return (
    <SAccordion
      title="Colors"
      collapsible
      defaultExpanded={defaultExpanded}
      disabled={disabled}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {COLOR_FIELDS.map(({ key, label, path }) => (
          <Box key={key}>
            <Typography
              variant="body2"
              component="label"
              htmlFor={`v2-color-${key}`}
              sx={{ mb: 0.5, display: 'block' }}
              aria-label={label}
            >
              {label}
            </Typography>
            <Box data-testid={`v2-color-input-${key}`}>
              <ColorInput
                value={colors[key]}
                onChange={handleColorChange(path)}
                disabled={disabled}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </SAccordion>
  );
};

export default V2ColorsSection;
